import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Heading, LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanRelatedClassList,
  fetchYowanRelatedProfile,
  fetchYowanRelatedSeminarList,
} from '../app/yowanContentClient';
import {
  buildStakeholderNavItems,
  getStakeholderClassesHref,
  getStakeholderRoleState,
  StakeholderGuardNotice,
  StakeholderShell,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
import {
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
const { yowanPageContent } = require('./pageContent');

const StakeholderDashboardPage = ({ guardOptions }) => {
  const content = yowanPageContent['/stakeholder'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const guard = useStakeholderGuard(guardOptions);
  const [summary, setSummary] = useState({
    classesCount: 0,
    seminarsCount: 0,
    instructorsCount: 0,
    organizersCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    Promise.all([
      fetchYowanRelatedClassList({ page: 1, pageSize: 1 }).catch(() => null),
      fetchYowanRelatedSeminarList({ page: 1, pageSize: 1 }).catch(() => null),
      fetchYowanRelatedProfile().catch(() => null),
    ])
      .then(([classesPayload, seminarsPayload, profilePayload]) => {
        if (!isMounted) {
          return;
        }

        setSummary({
          classesCount: Number(classesPayload?.count || classesPayload?.results?.length || 0),
          seminarsCount: Number(seminarsPayload?.count || seminarsPayload?.results?.length || 0),
          instructorsCount: Number(profilePayload?.instructors?.length || 0),
          organizersCount: Number(profilePayload?.organizers?.length || 0),
        });
      })
      .catch(() => {
        if (isMounted) {
          setLoadError(content?.loadErrorMessage || '関係者ページを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, guard.status]);

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  const roleState = getStakeholderRoleState(guard.currentUser);
  const dashboardNavItems = buildStakeholderNavItems(content, guard.currentUser);
  const dashboardGroups = [
    {
      title: '教室・講座 / セミナー',
      body: `${roleState.classesLabel}と${roleState.seminarsLabel}の内容、申込状況、申込メッセージを確認します。`,
      links: dashboardNavItems
        .filter((item) => item.href === '/stakeholder/classes' || item.href === '/stakeholder/seminars')
        .map((item) => ({
          ...item,
          metricKey: item.href === '/stakeholder/classes' ? 'classesCount' : 'seminarsCount',
        })),
    },
    {
      title: 'プロフィール',
      body: '公開プロフィールとして表示される情報を確認し、必要な範囲で更新します。',
      links: dashboardNavItems
        .filter((item) => item.href === '/stakeholder/instructor-profile' || item.href === '/stakeholder/organizer-profile')
        .map((item) => ({
          ...item,
          metricKey: item.href === '/stakeholder/instructor-profile' ? 'instructorsCount' : 'organizersCount',
        })),
    },
  ].filter((group) => group.links.length > 0);

  return (
    <StakeholderShell activeNavHref="/stakeholder" routeKey="/stakeholder" currentUser={guard.currentUser}>
      <div className="yowan-stakeholder-dashboard">
        {content?.header?.paragraphs?.length ? (
          <div className="yowan-admin-overview">
            {content.header.paragraphs.map((paragraph) => (
              <p key={paragraph} className="yowan-admin-overview__lead">
                {paragraph}
              </p>
            ))}
          </div>
        ) : null}
        {isLoading ? <LoadingSpinner label="関係者ページを読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError ? (
          <div className="yowan-admin-link-groups yowan-stakeholder-dashboard__links">
            {dashboardGroups.map((group) => (
              <section key={group.title} className="yowan-admin-link-groups__section">
                <Heading as="h2">{group.title}</Heading>
                <p className="yowan-admin-link-groups__lead">{group.body}</p>
                <ul className="yowan-admin-link-groups__list">
                  {group.links.map((link) => {
                    const count = summary[link.metricKey] ?? null;
                    const href = link.href === '/stakeholder/classes'
                      ? getStakeholderClassesHref(preservedDomainSearch)
                      : appendYowanPreviewQueryIfNeeded(link.href, preservedDomainSearch);

                    return (
                      <li key={link.label}>
                        <Link to={href}>
                          {link.label}
                        </Link>
                        {count !== null ? <span className="yowan-stakeholder-dashboard__link-count"> {count}件</span> : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        ) : null}
      </div>
    </StakeholderShell>
  );
};

export default StakeholderDashboardPage;
